/**
 * Seed Export Configuration
 *
 * ANALYSIS FINDINGS:
 * - Original component: ExportSeeds.tsx (212 lines)
 * - Filters: status (from useGetSeedFilters), class (TLD, Domain, CIDR, IPv4, IPv6)
 * - Columns: 10 exportable fields including seed-specific fields
 * - Storage key: ADVANCE_EXPORT_OPTIONS.seeds.v3
 * - Query: Simple Seed node with status and class filters
 *
 * MIGRATION NOTES:
 * - Seeds have different status values than Assets/Risks (A/F/D/R/etc)
 * - Class filter represents seed type (domain, tld, ip, cidr, etc)
 * - Email filter only applies to domain seeds
 * - Country filter only applies to IP seeds
 */

import {
  CheckCircleIcon,
  ClockIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { Frozen as FrozenIcon } from '@/components/chariot-ui';
import type {
  ExportColumnConfig,
  ExportOption,
  UniversalExportConfig,
  ValidationError,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { StatusChipVariant } from '@/components/legacy-ui/StatusChip';
import type { Seed } from '@/types';
import { SeedStatus, SeedStatusLabel } from '@/types';

/**
 * Seed class/type options
 * Matches the type filter in original ExportSeeds component
 */
const SEED_CLASS_OPTIONS = [
  { label: 'TLD', value: 'tld' },
  { label: 'Domain', value: 'domain' },
  { label: 'CIDR', value: 'cidr' },
  { label: 'IPv4', value: 'ipv4' },
  { label: 'IPv6', value: 'ipv6' },
] as const;

/**
 * Seed status styling map (matches SeedStatus.tsx patterns)
 */
const SEED_STATUS_STYLE: Record<
  string,
  { variant: StatusChipVariant; icon: React.ReactNode }
> = {
  [SeedStatus.Accept]: {
    variant: 'info',
    icon: React.createElement(CheckCircleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  [SeedStatus.ActivePassive]: {
    variant: 'info',
    icon: React.createElement(CheckCircleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  [SeedStatus.Pending]: {
    variant: 'high',
    icon: React.createElement(ClockIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Reject]: {
    variant: 'critical',
    icon: React.createElement(XCircleIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Freeze]: {
    variant: 'muted',
    icon: React.createElement(FrozenIcon, { className: 'size-3 shrink-0' }),
  },
  [SeedStatus.Delete]: {
    variant: 'muted',
    icon: React.createElement(TrashIcon, { className: 'size-3 shrink-0' }),
  },
};

/**
 * All seed status options (including Deleted).
 * createSeedExportConfig() filters Deleted for the UI.
 */
const ALL_SEED_STATUS_OPTIONS = Object.values(SeedStatus)
  .filter(
    // Exclude low-priority statuses per useGetSeedFilters
    value => value !== SeedStatus.PendingLow && value !== SeedStatus.AcceptLow
  )
  .map(value => ({
    label: SeedStatusLabel[value],
    value: value,
    ...SEED_STATUS_STYLE[value],
  }));

/**
 * Seed column configuration - 13 columns total
 * Maps to original ColumnOptions in ExportSeeds.tsx
 */
export const SEED_COLUMNS: readonly ExportColumnConfig[] = [
  { id: 'asset_identifier', label: 'Identifier', defaultSelected: true },
  { id: 'asset_name', label: 'Name', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'origins', label: 'Origins', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'registrar', label: 'Registrar', defaultSelected: true },
  { id: 'registrant', label: 'Registrant', defaultSelected: true },
  { id: 'country', label: 'Country', defaultSelected: true },
  { id: 'email', label: 'Email', defaultSelected: true },
  { id: 'class', label: 'Class', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'created', label: 'Created', defaultSelected: true },
  { id: 'visited', label: 'Visited', defaultSelected: true },
] as const;

/**
 * Base seed export configuration (shared structure, no status filtering applied).
 * Use createSeedExportConfig() to get a user-context-aware configuration.
 */
const seedExportConfigBase = {
  entityType: 'Seed' as const,
  apiEntityType: 'seed' as const,
  storageKey: 'ADVANCE_EXPORT_OPTIONS.seeds.v5',

  columns: SEED_COLUMNS,

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
    { label: 'PDF', value: 'pdf' },
  ] as const,
  defaultFormat: { label: 'CSV', value: 'csv' } as const,

  /**
   * Build graph query for seed export
   *
   * QUERY STRUCTURE:
   * - Labels: ['Seed']
   * - Filters: status, class (when selected)
   * - OrderBy: dns (ascending)
   *
   * BEHAVIOR:
   * - When selectedRows is empty: Apply all filters
   * - When selectedRows has values: Only export selected seeds
   */
  buildQuery: (
    filters: Readonly<Record<string, readonly Readonly<ExportOption>[]>>,
    _selectedRows: readonly string[]
  ) => {
    let builder = GraphQueryBuilder.create(['Seed']);

    // Add status filter if selected
    if (filters.status?.length > 0) {
      builder = builder.filter(
        'status',
        '=',
        filters.status.map(s => s.value)
      );
    }

    // Add class filter if selected
    if (filters.class?.length > 0) {
      builder = builder.filter(
        'class',
        '=',
        filters.class.map(c => c.value)
      );
    }

    // Set ordering (matches original: orderBy: 'dns', descending: false)
    return builder.orderBy('dns', false).build();
  },

  /**
   * Validate export selections
   *
   * VALIDATION RULES:
   * - At least one column must be selected
   * - If no rows selected, at least one filter must be selected
   */
  validateExport: (
    filters: Readonly<Record<string, readonly Readonly<ExportOption>[]>>,
    columns: readonly Readonly<ExportOption>[],
    selectedRows: readonly string[]
  ): readonly ValidationError[] | null => {
    const errors: ValidationError[] = [];

    // Validate columns
    if (columns.length === 0) {
      errors.push({
        field: 'columns' as const,
        message: 'Please select at least one column to export.',
      });
    }

    // Validate filters when exporting all
    if (selectedRows.length === 0) {
      const hasStatusFilter = filters.status && filters.status.length > 0;
      const hasClassFilter = filters.class && filters.class.length > 0;

      if (!hasStatusFilter && !hasClassFilter) {
        errors.push({
          field: 'filters' as const,
          message:
            'Please select at least one status or class filter when exporting all seeds.',
        });
      }
    }

    return errors.length > 0 ? errors : null;
  },
};

/**
 * Creates a user-context-aware seed export configuration.
 *
 * Non-Praetorian users should not see or export seeds with the Deleted ('D') status.
 *
 * @param isPraetorianUser - Whether the current user is a Praetorian employee
 * @returns Seed export configuration with appropriate status options
 *
 * @example
 * ```tsx
 * const { isPraetorianUser } = useAuth();
 * const config = createSeedExportConfig(isPraetorianUser);
 *
 * <UniversalExportModal config={config} ... />
 * ```
 */
export function createSeedExportConfig(): UniversalExportConfig<Seed> {
  const statusOptions = ALL_SEED_STATUS_OPTIONS.filter(
    opt => opt.value !== SeedStatus.Delete
  );

  return {
    ...seedExportConfigBase,
    filterSections: [
      {
        id: 'status',
        label: 'Status',
        options: statusOptions,
        defaultSelected: statusOptions, // Default: all visible statuses
        showWhenSelected: false, // Hide when rows are selected
      },
      {
        id: 'class',
        label: 'Class',
        options: SEED_CLASS_OPTIONS,
        defaultSelected: SEED_CLASS_OPTIONS, // Default: all classes
        showWhenSelected: false, // Hide when rows are selected
      },
    ],
  };
}

/**
 * Default seed export config for backwards compatibility.
 * @deprecated Use createSeedExportConfig() to get a config.
 */
export const seedExportConfig: UniversalExportConfig<Seed> =
  createSeedExportConfig();
